import React from 'react';
import { Button, Alert, Spinner } from '@parsina/framework';
import { PhoneOff, Video } from 'lucide-react';
import { C, SHADOW } from '../../../constants/theme';
import { useTranslation } from '../../../i18n/I18nProvider.jsx';
import { CALL_STARTING } from '../../../hooks/useVideoVisit.js';

// ویزیت تصویری — the call, drawn OVER the visit workspace.
//
// ── Why an overlay and not a tab or a route ─────────────────────────────────
// A dentist on a video visit is still working: charting the tooth the patient
// is describing, writing the subjective, reading the plan. A tab would put the
// call and the chart in the same space and force a choice between them, and a
// route would take the workspace off screen entirely (and would need a nav
// entry that CORE.md §8 refuses for an unbuilt screen — this one has no screen
// of its own by design).
//
// So it floats: a panel anchored to the corner, over the left column, with the
// odontogram and the subjective still visible and still usable beneath it.
//
// ── The container node is never unmounted mid-call ──────────────────────────
// `containerRef` is where Jitsi attaches its iframe. React must not own that
// subtree's children — hence an empty div and no children of our own inside it.
// The panel's chrome sits AROUND it, never within.

/** The floating panel's footprint. Deliberately small: it is not the screen. */
const PANEL_WIDTH = 380;
const VIDEO_HEIGHT = 260;

export default function VisitCall({ call, patientName }) {
  const { t } = useTranslation();
  const { state, error, live, containerRef, stop } = call;

  // Nothing to draw until the dentist has asked for a call. `idle` with no
  // error means the button in the header has not been pressed.
  if (state === 'idle') return null;

  return (
    <aside
      aria-label={t('visit.call.panelLabel')}
      style={{
        position: 'fixed',
        insetInlineStart: 24,
        bottom: 24,
        width: PANEL_WIDTH,
        maxWidth: 'calc(100vw - 48px)',
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        boxShadow: SHADOW.floating,
        overflow: 'hidden',
        // Above the workspace, below a modal: a ConfirmDialog for «پایان جلسه»
        // must still be able to cover this.
        zIndex: 40,
      }}
    >
      <header
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, padding: '10px 12px',
          background: C.navy, color: C.card,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Video size={16} aria-hidden />
          <span style={{
            fontSize: 13, fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {patientName || t('visit.call.title')}
          </span>
        </span>
        {/* «پایان تماس» ends the CALL, not the visit. The two are deliberately
            different words on this screen — «پایان جلسه» is one-way and closes
            the record, and a dentist reaching to hang up must not be able to
            close the appointment by mistake. */}
        {/* `size="sm"` is 36px in the kit, under the 2.75rem touch floor, so
            the size is stated rather than inherited (tests/mobileComposition). */}
        <Button
          variant="danger"
          size="sm"
          onClick={stop}
          className="min-h-11"
        >
          <PhoneOff size={14} aria-hidden />
          {t('visit.call.hangUp')}
        </Button>
      </header>

      {error && (
        <div style={{ padding: 12 }} role="alert">
          <Alert
            variant="error"
            title={t(`visit.call.error.${error}`)}
          />
        </div>
      )}

      {/* Kept MOUNTED across states — Jitsi owns this node's children, and
          unmounting it mid-connection would pull the iframe out from under a
          conference that is still negotiating. Height collapses to zero while
          there is nothing in it. */}
      <div
        ref={containerRef}
        style={{
          height: error ? 0 : VIDEO_HEIGHT,
          background: C.ink,
          position: 'relative',
        }}
      >
        {state === CALL_STARTING && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 8,
            color: C.card,
          }}>
            <Spinner />
            <span style={{ fontSize: 12 }}>{t('visit.call.connecting')}</span>
          </div>
        )}
      </div>

      {live && (
        <p style={{
          margin: 0, padding: '8px 12px',
          fontSize: 11, color: C.ink2, background: C.bg,
        }}>
          {/* Said out loud on every call, because a patient cannot see this
              screen and a dentist should never have to guess. */}
          {t('visit.call.notRecorded')}
        </p>
      )}
    </aside>
  );
}
